import { useCallback, useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  EQUIPMENT_CONDITIONS,
  EQUIPMENT_DURATIONS,
  EQUIPMENT_OPTIONS,
  KHITAN_CHILD_STATUSES,
  LAYANAN_CARE_TYPES,
  LAYANAN_CONDITIONS,
  LAYANAN_DESTINATIONS,
  LAYANAN_STATUSES,
  canChangeLayananStatus,
  canCreateLayanan,
  canEditData,
  canEditLayanan,
  formatDate,
  optionLabel,
  today,
} from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2, Pencil, Search, Trash2, Upload, Download, FileSpreadsheet, CheckCircle2, Eye, Send } from "lucide-react";

const dayFromDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("id-ID", { weekday: "long" });
};

const emptyForm = {
  patient_name: "",
  desa: "",
  dukuh: "",
  rt: "",
  rw: "",
  date: today(),
  time: "",
  destination: "rs",
  destination_detail: "",
  care_type: "rawat_jalan",
  patient_condition: "berbaring",
  family_phone: "",
  status: "baru",
};

const STATUS_COLORS = {
  baru: "bg-yellow-100 text-yellow-800",
  diproses: "bg-sky-100 text-sky-800",
  selesai: "bg-emerald-100 text-emerald-800",
  batal: "bg-slate-100 text-slate-500",
};

const downloadSurat = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const link = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = link;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(link);
    toast.success("Surat layanan berhasil diunduh (PDF)");
  } catch (e) {
    toast.error("Gagal mengunduh surat layanan");
  }
};

const KHITAN_DOC_LABELS = { akta: "Akta/KIA", ktp: "KTP", kk: "KK", rekomendasi: "Rekomendasi", persetujuan: "Persetujuan" };

function KhitanList({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const colCount = user?.role === "admin" ? 7 : 6;
  const canExportLayanan = ["admin", "pengurus", "operator", "koordinator_layanan"].includes(user?.role);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get("/khitan-requests", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleExport = async (format) => {
    try {
      const res = await api.get("/khitan-requests/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-khitan-sehat-gratis.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data khitan berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data khitan");
    }
  };

  const handleStatus = async (r, status) => {
    try {
      await api.patch(`/khitan-requests/${r.request_id}/status`, { status });
      toast.success(`Status diubah menjadi ${optionLabel(LAYANAN_STATUSES, status)}`);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (r) => {
    if (confirmDelete !== r.request_id) {
      setConfirmDelete(r.request_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/khitan-requests/${r.request_id}`);
      toast.success("Pendaftaran dihapus");
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (r) => {
    setEditForm({
      participant_name: r.participant_name || "",
      nik: r.nik || "",
      parent_name: r.parent_name || "",
      child_status: r.child_status || "yatim",
      parent_phone: r.parent_phone || "",
      desa: r.desa || "",
      dukuh: r.dukuh || "",
      rt: r.rt || "",
      rw: r.rw || "",
      date: r.date || "",
      time: r.time || "",
      status: r.status || "baru",
    });
    setEditItem(r);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/khitan-requests/${editItem.request_id}`, { ...editForm, day: dayFromDate(editForm.date) });
      toast.success("Pendaftaran khitan diperbarui");
      setEditItem(null);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () => setSelected((prev) => (prev.length === items.length ? [] : items.map((r) => r.request_id)));
  const handleBulkDelete = async () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      setTimeout(() => setConfirmBulk(false), 3000);
      return;
    }
    try {
      const { data } = await api.post("/khitan-requests/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} pendaftaran dihapus`);
      setSelected([]);
      setConfirmBulk(false);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
    <Card data-testid="khitan-list">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-lg flex-1">Pendaftaran Khitan Sehat Gratis ({items.length})</CardTitle>
          {user?.role === "admin" && selected.length > 0 && (
            <Button type="button" size="sm" variant={confirmBulk ? "destructive" : "outline"} data-testid="khitan-bulk-delete-button" onClick={handleBulkDelete}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />{confirmBulk ? `Yakin? Hapus ${selected.length} Data` : `Hapus Terpilih (${selected.length})`}
            </Button>
          )}
          {canExportLayanan && (
            <>
              <Button type="button" variant="outline" size="sm" data-testid="khitan-export-pdf-button" onClick={() => handleExport("pdf")}>
                <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="khitan-export-excel-button" onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
              </Button>
            </>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="khitan-search-input"
              placeholder="Cari nama/NIK..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[200px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[560px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                {user?.role === "admin" && (
                  <TableHead className="w-8">
                    <input type="checkbox" data-testid="khitan-select-all" className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={items.length > 0 && selected.length === items.length} onChange={toggleSelectAll} />
                  </TableHead>
                )}
                <TableHead>Peserta</TableHead>
                <TableHead>Orangtua/Wali</TableHead>
                <TableHead>Jadwal</TableHead>
                <TableHead>Dokumen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline-block" />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="khitan-empty">
                    Belum ada pendaftaran khitan
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => (
                  <TableRow key={r.request_id} data-testid={`khitan-row-${r.request_id}`}>
                    {user?.role === "admin" && (
                      <TableCell>
                        <input type="checkbox" data-testid={`khitan-select-${r.request_id}`} className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={selected.includes(r.request_id)} onChange={() => toggleSelect(r.request_id)} />
                      </TableCell>
                    )}
                    <TableCell>
                      <p className="text-sm font-medium text-slate-800">{r.participant_name}</p>
                      <p className="text-xs text-slate-400">NIK: {r.nik || "-"} · {optionLabel(KHITAN_CHILD_STATUSES, r.child_status)}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-700">{r.parent_name}</p>
                      <p className="text-xs text-slate-400">{r.parent_phone || "-"}</p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {r.day}, {formatDate(r.date)}{r.time ? ` · ${r.time}` : ""}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.documents || {}).map(([k, doc]) => (
                          <a
                            key={k}
                            href={`${API}/files/${doc.path}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`khitan-doc-${r.request_id}-${k}`}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors duration-200 hover:bg-emerald-100 hover:text-emerald-800"
                          >
                            <FileText className="h-3 w-3" />{KHITAN_DOC_LABELS[k] || k}
                          </a>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={r.status || "baru"} onValueChange={(v) => handleStatus(r, v)} disabled={!canChangeLayananStatus(user?.role)}>
                        <SelectTrigger className={`h-7 w-[110px] text-xs font-semibold ${STATUS_COLORS[r.status || "baru"]}`} data-testid={`khitan-status-${r.request_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LAYANAN_STATUSES.map((s) => (
                            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {user?.role === "admin" && (
                          <button
                            data-testid={`khitan-edit-${r.request_id}`}
                            title="Ubah data"
                            onClick={() => openEdit(r)}
                            className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {canEditData(user?.role) && (
                          <button
                            data-testid={`khitan-surat-${r.request_id}`}
                            title="Unduh Surat Layanan (PDF)"
                            onClick={() => downloadSurat(`/khitan-requests/${r.request_id}/surat`, `surat-khitan-${r.request_id}.pdf`)}
                            className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                        )}
                        {canEditData(user?.role) && (
                        <button
                          data-testid={`khitan-delete-${r.request_id}`}
                          onClick={() => handleDelete(r)}
                          className={`rounded-md p-1.5 transition-colors duration-200 ${
                            confirmDelete === r.request_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                          }`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
    <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="khitan-edit-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Data Khitan</DialogTitle>
        </DialogHeader>
        {editItem && (
        <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3" data-testid="khitan-edit-form">
          <div className="space-y-1.5">
            <Label className="text-xs">Nama Peserta *</Label>
            <Input data-testid="khitan-edit-name-input" value={editForm.participant_name} onChange={(e) => setEditForm({ ...editForm, participant_name: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">NIK</Label>
            <Input data-testid="khitan-edit-nik-input" value={editForm.nik} onChange={(e) => setEditForm({ ...editForm, nik: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nama Orangtua / Wali</Label>
            <Input data-testid="khitan-edit-parent-input" value={editForm.parent_name} onChange={(e) => setEditForm({ ...editForm, parent_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status Anak</Label>
            <Select value={editForm.child_status} onValueChange={(v) => setEditForm({ ...editForm, child_status: v })}>
              <SelectTrigger data-testid="khitan-edit-child-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KHITAN_CHILD_STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. WA Orangtua / Wali</Label>
            <Input data-testid="khitan-edit-phone-input" value={editForm.parent_phone} onChange={(e) => setEditForm({ ...editForm, parent_phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desa</Label>
            <Input data-testid="khitan-edit-desa-input" value={editForm.desa} onChange={(e) => setEditForm({ ...editForm, desa: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Dukuh</Label>
            <Input data-testid="khitan-edit-dukuh-input" value={editForm.dukuh} onChange={(e) => setEditForm({ ...editForm, dukuh: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">RT</Label>
              <Input data-testid="khitan-edit-rt-input" value={editForm.rt} onChange={(e) => setEditForm({ ...editForm, rt: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RW</Label>
              <Input data-testid="khitan-edit-rw-input" value={editForm.rw} onChange={(e) => setEditForm({ ...editForm, rw: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tanggal</Label>
            <Input type="date" data-testid="khitan-edit-date-input" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Jam</Label>
            <Input type="time" data-testid="khitan-edit-time-input" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">Status</Label>
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger data-testid="khitan-edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LAYANAN_STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="khitan-edit-save" disabled={editSaving} className="col-span-2 bg-emerald-800 hover:bg-emerald-700 text-white">
            {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

const emptyLoanForm = {
  name: "",
  nik: "",
  birth_place: "",
  birth_date: "",
  dukuh: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "",
  equipment: "kursi_roda",
  user_condition: "sakit",
  duration: "1_bulan",
};

const calcAge = (birthDate) => {
  if (!birthDate) return "";
  const bd = new Date(`${birthDate}T00:00:00`);
  if (isNaN(bd.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - bd.getFullYear();
  if (now.getMonth() < bd.getMonth() || (now.getMonth() === bd.getMonth() && now.getDate() < bd.getDate())) age -= 1;
  return age >= 0 ? age : "";
};

function EquipmentLoans({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyLoanForm);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const colCount = user?.role === "admin" ? 7 : 6;
  const canExportLayanan = ["admin", "pengurus", "operator", "koordinator_layanan"].includes(user?.role);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get("/equipment-loans", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/equipment-loans/${editingId}`, form);
        toast.success("Data peminjaman diperbarui");
      } else {
        await api.post("/equipment-loans", form);
        toast.success("Data pinjam alat kesehatan tersimpan");
      }
      setEditingId(null);
      setForm(emptyLoanForm);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.loan_id);
    setForm({
      name: r.name || "",
      nik: r.nik || "",
      birth_place: r.birth_place || "",
      birth_date: r.birth_date || "",
      dukuh: r.dukuh || "",
      desa: r.desa || "",
      rt: r.rt || "",
      rw: r.rw || "",
      kecamatan: r.kecamatan || "",
      phone: r.phone || "",
      equipment: r.equipment,
      user_condition: r.user_condition,
      duration: r.duration,
      status: r.status || "baru",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/equipment-loans/${editingId}`, form);
      toast.success("Data peminjaman diperbarui");
      setEditOpen(false);
      setEditingId(null);
      setForm(emptyLoanForm);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (r, status) => {
    try {
      await api.patch(`/equipment-loans/${r.loan_id}/status`, { status });
      toast.success(`Status diubah menjadi ${optionLabel(LAYANAN_STATUSES, status)}`);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (r) => {
    if (confirmDelete !== r.loan_id) {
      setConfirmDelete(r.loan_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/equipment-loans/${r.loan_id}`);
      toast.success("Data peminjaman dihapus");
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await api.get("/equipment-loans/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-pinjam-alat-kesehatan.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data pinjam alat berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data pinjam alat");
    }
  };

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () => setSelected((prev) => (prev.length === items.length ? [] : items.map((r) => r.loan_id)));
  const handleBulkDelete = async () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      setTimeout(() => setConfirmBulk(false), 3000);
      return;
    }
    try {
      const { data } = await api.post("/equipment-loans/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} data peminjaman dihapus`);
      setSelected([]);
      setConfirmBulk(false);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const age = calcAge(form.birth_date);

  return (
    <div data-testid="alat-section">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-lg flex-1">Daftar Pinjam Alat Kesehatan ({items.length})</CardTitle>
            {user?.role === "admin" && selected.length > 0 && (
              <Button type="button" size="sm" variant={confirmBulk ? "destructive" : "outline"} data-testid="alat-bulk-delete-button" onClick={handleBulkDelete}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />{confirmBulk ? `Yakin? Hapus ${selected.length} Data` : `Hapus Terpilih (${selected.length})`}
              </Button>
            )}
            {canExportLayanan && (
              <>
                <Button type="button" variant="outline" size="sm" data-testid="alat-export-pdf-button" onClick={() => handleExport("pdf")}>
                  <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid="alat-export-excel-button" onClick={() => handleExport("xlsx")}>
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
                </Button>
              </>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                data-testid="alat-search-input"
                placeholder="Cari nama/desa/NIK..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-[200px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[560px] overflow-auto">
            <Table data-testid="alat-table">
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  {user?.role === "admin" && (
                    <TableHead className="w-8">
                      <input type="checkbox" data-testid="alat-select-all" className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={items.length > 0 && selected.length === items.length} onChange={toggleSelectAll} />
                    </TableHead>
                  )}
                  <TableHead>Peminjam</TableHead>
                  <TableHead>Alamat</TableHead>
                  <TableHead>Alat & Kondisi</TableHead>
                  <TableHead>Jangka Waktu</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                      <Loader2 className="h-5 w-5 animate-spin inline-block" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="alat-empty">
                      Belum ada data peminjaman alat
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((r) => (
                    <TableRow key={r.loan_id} data-testid={`alat-row-${r.loan_id}`}>
                      {user?.role === "admin" && (
                        <TableCell>
                          <input type="checkbox" data-testid={`alat-select-${r.loan_id}`} className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={selected.includes(r.loan_id)} onChange={() => toggleSelect(r.loan_id)} />
                        </TableCell>
                      )}
                      <TableCell>
                        <p className="text-sm font-medium text-slate-800">{r.name}</p>
                        {r.nik && <p className="text-xs text-slate-400">NIK: {r.nik}</p>}
                        <p className="text-xs text-slate-400">
                          {[r.birth_place, r.birth_date && formatDate(r.birth_date)].filter(Boolean).join(", ")}
                          {r.usia != null ? ` (${r.usia} th)` : ""}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm">
                        {[r.dukuh && `Dk. ${r.dukuh}`, r.desa && `Desa ${r.desa}`, (r.rt || r.rw) && `RT ${r.rt || "-"}/RW ${r.rw || "-"}`, r.kecamatan && `Kec. ${r.kecamatan}`].filter(Boolean).join(", ") || "-"}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {optionLabel(EQUIPMENT_OPTIONS, r.equipment)} · {optionLabel(EQUIPMENT_CONDITIONS, r.user_condition)}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{optionLabel(EQUIPMENT_DURATIONS, r.duration)}</TableCell>
                      <TableCell>
                        <Select value={r.status || "baru"} onValueChange={(v) => handleStatusChange(r, v)} disabled={!canChangeLayananStatus(user?.role)}>
                          <SelectTrigger className={`h-7 w-[110px] text-xs font-semibold ${STATUS_COLORS[r.status || "baru"]}`} data-testid={`alat-status-${r.loan_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LAYANAN_STATUSES.map((s) => (
                              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {user?.role === "admin" && (
                            <button
                              data-testid={`alat-edit-${r.loan_id}`}
                              title="Ubah data"
                              onClick={() => handleEdit(r)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {canEditData(user?.role) && (
                            <button
                              data-testid={`alat-surat-${r.loan_id}`}
                              title="Unduh Surat Layanan (PDF)"
                              onClick={() => downloadSurat(`/equipment-loans/${r.loan_id}/surat`, `surat-pinjam-alat-${r.loan_id}.pdf`)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                          {canEditData(user?.role) && (
                            <button
                              data-testid={`alat-delete-${r.loan_id}`}
                              onClick={() => handleDelete(r)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === r.loan_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="alat-edit-dialog">
          <DialogHeader>
            <DialogTitle>Ubah Data Peminjaman</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3" data-testid="alat-edit-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Nama *</Label>
              <Input data-testid="alat-edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">NIK</Label>
              <Input data-testid="alat-edit-nik-input" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tempat Lahir</Label>
              <Input data-testid="alat-edit-birth-place-input" value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tanggal Lahir</Label>
              <Input type="date" data-testid="alat-edit-birth-date-input" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Usia (otomatis)</Label>
              <Input data-testid="alat-edit-usia-input" value={age !== "" ? `${age} tahun` : ""} readOnly className="bg-slate-50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Dukuh</Label>
              <Input data-testid="alat-edit-dukuh-input" value={form.dukuh} onChange={(e) => setForm({ ...form, dukuh: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Desa</Label>
              <Input data-testid="alat-edit-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RT</Label>
              <Input data-testid="alat-edit-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">RW</Label>
                <Input data-testid="alat-edit-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Kecamatan</Label>
                <Input data-testid="alat-edit-kecamatan-input" value={form.kecamatan} onChange={(e) => setForm({ ...form, kecamatan: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">No. WhatsApp</Label>
              <Input data-testid="alat-edit-phone-input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Alat Bantu Kesehatan</Label>
              <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
                <SelectTrigger data-testid="alat-edit-equipment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kondisi Pengguna</Label>
              <Select value={form.user_condition} onValueChange={(v) => setForm({ ...form, user_condition: v })}>
                <SelectTrigger data-testid="alat-edit-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CONDITIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Jangka Waktu Pinjam</Label>
              <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                <SelectTrigger data-testid="alat-edit-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_DURATIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status || "baru"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="alat-edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LAYANAN_STATUSES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="alat-edit-save" disabled={saving} className="col-span-2 bg-emerald-800 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KARTU_DOC_LABELS = { rekomendasi_url: "Rekomendasi", ktp_url: "KTP" };

const emptyKartuForm = {
  name: "",
  nik: "",
  gender: "L",
  birth_date: "",
  phone: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "",
  kabupaten: "",
  rm: "",
  valid_until: "",
  status: "diajukan",
};

function KartuSehatList({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [confirmSendPdf, setConfirmSendPdf] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyKartuForm);
  const [editSaving, setEditSaving] = useState(false);
  const colCount = 7;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get("/kartu-sehat", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleExport = async (format) => {
    try {
      const res = await api.get("/kartu-sehat/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-kartu-sehat-gratis.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data kartu sehat berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data kartu sehat");
    }
  };

  const openEdit = (r) => {
    setEditForm({
      name: r.name || "",
      nik: r.nik || "",
      gender: r.gender || "L",
      birth_date: r.birth_date || "",
      phone: r.phone || "",
      desa: r.desa || "",
      rt: r.rt || "",
      rw: r.rw || "",
      kecamatan: r.kecamatan || "",
      kabupaten: r.kabupaten || "",
      rm: r.rm || "",
      valid_until: r.valid_until || "",
      status: r.status || "diajukan",
    });
    setEditItem(r);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/kartu-sehat/${editItem.card_id}`, editForm);
      toast.success("Data kartu diperbarui");
      setEditItem(null);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleApprove = async (r) => {
    if (!r.rm || !r.valid_until) {
      toast.error("Isi No. Rekam Medis & Masa Berlaku terlebih dahulu melalui tombol ubah (pensil)");
      return;
    }
    if (confirmApprove !== r.card_id) {
      setConfirmApprove(r.card_id);
      setTimeout(() => setConfirmApprove(null), 4000);
      return;
    }
    try {
      const { data } = await api.post(`/kartu-sehat/${r.card_id}/approve`);
      toast.success(data.wa?.ok ? "Kartu disetujui & kartu digital terkirim ke WA pemohon" : "Kartu disetujui (WA tidak terkirim)");
      setConfirmApprove(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (r) => {
    if (confirmDelete !== r.card_id) {
      setConfirmDelete(r.card_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/kartu-sehat/${r.card_id}`);
      toast.success("Data kartu dihapus");
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadKartu = async (r) => {
    try {
      const path = (r.card_url || "").split("/api/files/")[1];
      if (!path) throw new Error("no path");
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kartu-sehat-gratis-${(r.name || r.card_id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Kartu Sehat Gratis berhasil diunduh (PNG)");
    } catch (e) {
      toast.error("Gagal mengunduh kartu digital");
    }
  };

  const downloadKartuPdf = async (r) => {
    try {
      const res = await api.get(`/kartu-sehat/${r.card_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kartu-sehat-gratis-${(r.name || r.card_id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF Kartu Sehat Gratis berhasil diunduh");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const kirimKartuPdf = async (r) => {
    if (confirmSendPdf !== r.card_id) {
      setConfirmSendPdf(r.card_id);
      setTimeout(() => setConfirmSendPdf(null), 4000);
      return;
    }
    try {
      const { data } = await api.post(`/kartu-sehat/${r.card_id}/kirim-pdf`);
      toast.success(data.wa?.ok ? "PDF kartu terkirim ke WA pemohon" : `PDF tergenerate, tetapi WA gagal: ${data.wa?.reason || "-"}`);
      setConfirmSendPdf(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
    <Card data-testid="kartu-list">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-lg flex-1">Pemohon Kartu Sehat Gratis ({items.length})</CardTitle>
          <Button type="button" variant="outline" size="sm" data-testid="kartu-export-pdf-button" onClick={() => handleExport("pdf")}>
            <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="kartu-export-excel-button" onClick={() => handleExport("xlsx")}>
            <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
          </Button>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="kartu-search-input"
              placeholder="Cari nama/NIK/RM..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[200px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[560px] overflow-auto">
          <Table data-testid="kartu-table">
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead>Pemohon</TableHead>
                <TableHead>Alamat</TableHead>
                <TableHead>No. WA</TableHead>
                <TableHead>L/P · Tgl Lahir</TableHead>
                <TableHead>Dokumen</TableHead>
                <TableHead>Status Kartu</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline-block" />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="kartu-empty">
                    Belum ada pemohon kartu sehat
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => (
                  <TableRow key={r.card_id} data-testid={`kartu-row-${r.card_id}`}>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-800">{r.name}</p>
                      <p className="text-xs text-slate-400">NIK: {r.nik || "-"} · RM: {r.rm || "—"}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      {[r.desa && `Desa ${r.desa}`, (r.rt || r.rw) && `RT ${r.rt || "-"}/RW ${r.rw || "-"}`, r.kecamatan && `Kec. ${r.kecamatan}`, r.kabupaten && `Kab. ${r.kabupaten}`].filter(Boolean).join(", ") || "-"}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{r.phone || "-"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {r.gender || "-"} · {r.birth_date ? formatDate(r.birth_date) : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {["rekomendasi_url", "ktp_url"].filter((k) => r[k]).map((k) => (
                          <a
                            key={k}
                            href={`${API}/files/${r[k]}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`kartu-doc-${r.card_id}-${k === "ktp_url" ? "ktp" : "rekomendasi"}`}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors duration-200 hover:bg-emerald-100 hover:text-emerald-800"
                          >
                            <FileText className="h-3 w-3" />{KARTU_DOC_LABELS[k]}
                          </a>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "disetujui" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`kartu-status-${r.card_id}`}>
                        {r.status === "disetujui" ? `Disetujui${r.valid_until ? ` s.d. ${formatDate(r.valid_until)}` : ""}` : "Diajukan"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`kartu-edit-${r.card_id}`}
                          title="Ubah data (termasuk No. RM & Masa Berlaku)"
                          onClick={() => openEdit(r)}
                          className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {r.status !== "disetujui" ? (
                          <button
                            data-testid={`kartu-approve-${r.card_id}`}
                            title="Kartu Disetujui — kirim kartu digital ke WA pemohon"
                            onClick={() => handleApprove(r)}
                            className={`rounded-md p-1.5 transition-colors duration-200 ${confirmApprove === r.card_id ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-700"}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        ) : r.card_url ? (
                          <>
                            <a
                              href={r.card_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`kartu-view-${r.card_id}`}
                              title="Lihat kartu digital"
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Eye className="h-4 w-4" />
                            </a>
                            <button
                              data-testid={`kartu-download-${r.card_id}`}
                              title="Unduh Kartu Sehat Gratis (PNG)"
                              onClick={() => downloadKartu(r)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`kartu-pdf-${r.card_id}`}
                              title="Unduh Kartu Sehat Gratis (PDF)"
                              onClick={() => downloadKartuPdf(r)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`kartu-kirim-pdf-${r.card_id}`}
                              title="Kirim PDF kartu ke WA pemohon (klik 2x)"
                              onClick={() => kirimKartuPdf(r)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${confirmSendPdf === r.card_id ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-700"}`}
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          </>
                        ) : null}
                        <button
                          data-testid={`kartu-delete-${r.card_id}`}
                          title="Hapus data"
                          onClick={() => handleDelete(r)}
                          className={`rounded-md p-1.5 transition-colors duration-200 ${
                            confirmDelete === r.card_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                          }`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
    <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kartu-edit-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Data Kartu Sehat — {editItem?.name}</DialogTitle>
        </DialogHeader>
        {editItem && (
        <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3" data-testid="kartu-edit-form">
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">Nama Pemohon *</Label>
            <Input data-testid="kartu-edit-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">NIK</Label>
            <Input data-testid="kartu-edit-nik-input" value={editForm.nik} onChange={(e) => setEditForm({ ...editForm, nik: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Jenis Kelamin</Label>
            <Select value={editForm.gender} onValueChange={(v) => setEditForm({ ...editForm, gender: v })}>
              <SelectTrigger data-testid="kartu-edit-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="L">Laki-laki</SelectItem>
                <SelectItem value="P">Perempuan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tanggal Lahir</Label>
            <Input type="date" data-testid="kartu-edit-birth-date-input" value={editForm.birth_date} onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. WhatsApp</Label>
            <Input data-testid="kartu-edit-phone-input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desa</Label>
            <Input data-testid="kartu-edit-desa-input" value={editForm.desa} onChange={(e) => setEditForm({ ...editForm, desa: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">RT</Label>
              <Input data-testid="kartu-edit-rt-input" value={editForm.rt} onChange={(e) => setEditForm({ ...editForm, rt: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RW</Label>
              <Input data-testid="kartu-edit-rw-input" value={editForm.rw} onChange={(e) => setEditForm({ ...editForm, rw: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kecamatan</Label>
            <Input data-testid="kartu-edit-kecamatan-input" value={editForm.kecamatan} onChange={(e) => setEditForm({ ...editForm, kecamatan: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kabupaten</Label>
            <Input data-testid="kartu-edit-kabupaten-input" value={editForm.kabupaten} onChange={(e) => setEditForm({ ...editForm, kabupaten: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. Rekam Medis (RM)</Label>
            <Input data-testid="kartu-edit-rm-input" placeholder="diisi oleh petugas" value={editForm.rm} onChange={(e) => setEditForm({ ...editForm, rm: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Masa Berlaku Kartu</Label>
            <Input type="date" data-testid="kartu-edit-valid-until-input" value={editForm.valid_until} onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">Status Kartu</Label>
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger data-testid="kartu-edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diajukan">Diajukan</SelectItem>
                <SelectItem value="disetujui">Disetujui</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="kartu-edit-save" disabled={editSaving} className="col-span-2 bg-emerald-800 hover:bg-emerald-700 text-white">
            {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}


const emptyUsgForm = {
  name: "",
  nik: "",
  birth_date: "",
  phone: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "",
  kabupaten: "",
  rm: "",
  status: "diajukan",
};

function UsgList({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyUsgForm);
  const [editSaving, setEditSaving] = useState(false);
  const colCount = 7;
  const wibToday = new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
  const approvedToday = items.filter((i) => i.status === "disetujui" && i.approved_date === wibToday).length;
  const quotaFull = approvedToday >= 20;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get("/usg", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleExport = async (format) => {
    try {
      const res = await api.get("/usg/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-usg-gratis.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data pemohon USG berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data pemohon USG");
    }
  };

  const openEdit = (r) => {
    setEditForm({
      name: r.name || "",
      nik: r.nik || "",
      birth_date: r.birth_date || "",
      phone: r.phone || "",
      desa: r.desa || "",
      rt: r.rt || "",
      rw: r.rw || "",
      kecamatan: r.kecamatan || "",
      kabupaten: r.kabupaten || "",
      rm: r.rm || "",
      status: r.status || "diajukan",
    });
    setEditItem(r);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/usg/${editItem.usg_id}`, editForm);
      toast.success("Data pemohon diperbarui");
      setEditItem(null);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleApprove = async (r) => {
    if (confirmApprove !== r.usg_id) {
      setConfirmApprove(r.usg_id);
      setTimeout(() => setConfirmApprove(null), 4000);
      return;
    }
    try {
      const { data } = await api.post(`/usg/${r.usg_id}/approve`);
      toast.success(data.wa?.ok
        ? `Disetujui · Antrian ${data.queue_no} · ${data.visit_day}, ${formatDate(data.visit_date)} · ${data.visit_time} — WA terkirim ke pemohon`
        : "Disetujui (WA tidak terkirim)");
      setConfirmApprove(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmApprove(null);
    }
  };

  const handleDelete = async (r) => {
    if (confirmDelete !== r.usg_id) {
      setConfirmDelete(r.usg_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/usg/${r.usg_id}`);
      toast.success("Data pemohon dihapus");
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
    <Card data-testid="usg-list">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-lg flex-1">Pemohon Ibu Hamil & USG Gratis ({items.length})</CardTitle>
          <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${quotaFull ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}`} data-testid="usg-quota-badge">
            Kuota hari ini: {approvedToday}/20
          </span>
          <Button type="button" variant="outline" size="sm" data-testid="usg-export-pdf-button" onClick={() => handleExport("pdf")}>
            <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="usg-export-excel-button" onClick={() => handleExport("xlsx")}>
            <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
          </Button>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="usg-search-input"
              placeholder="Cari nama/NIK/RM..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[200px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[560px] overflow-auto">
          <Table data-testid="usg-table">
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead>Pemohon</TableHead>
                <TableHead>Alamat</TableHead>
                <TableHead>No. WA</TableHead>
                <TableHead>Tgl Lahir</TableHead>
                <TableHead>Dokumen</TableHead>
                <TableHead>Status & Jadwal Besuk</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline-block" />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="usg-empty">
                    Belum ada pemohon USG
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => (
                  <TableRow key={r.usg_id} data-testid={`usg-row-${r.usg_id}`}>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-800">{r.name}</p>
                      <p className="text-xs text-slate-400">NIK: {r.nik || "-"} · RM: {r.rm || "—"}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      {[r.desa && `Desa ${r.desa}`, (r.rt || r.rw) && `RT ${r.rt || "-"}/RW ${r.rw || "-"}`, r.kecamatan && `Kec. ${r.kecamatan}`, r.kabupaten && `Kab. ${r.kabupaten}`].filter(Boolean).join(", ") || "-"}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{r.phone || "-"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{r.birth_date ? formatDate(r.birth_date) : "-"}</TableCell>
                    <TableCell>
                      {r.ktp_url ? (
                        <a
                          href={`${API}/files/${r.ktp_url}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`usg-doc-${r.usg_id}-ktp`}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors duration-200 hover:bg-emerald-100 hover:text-emerald-800"
                        >
                          <FileText className="h-3 w-3" />KTP
                        </a>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "disetujui" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`usg-status-${r.usg_id}`}>
                        {r.status === "disetujui" ? "Disetujui" : "Diajukan"}
                      </span>
                      {r.status === "disetujui" && (
                        <p className="text-[11px] text-slate-500 mt-1" data-testid={`usg-jadwal-${r.usg_id}`}>
                          Antrian {r.queue_no || "-"} · {r.visit_day}, {r.visit_date ? formatDate(r.visit_date) : "-"} · {r.visit_time}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`usg-edit-${r.usg_id}`}
                          title="Ubah data (termasuk No. RM)"
                          onClick={() => openEdit(r)}
                          className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {r.status !== "disetujui" && (
                          <button
                            data-testid={`usg-approve-${r.usg_id}`}
                            title="Disetujui — kirim jadwal besuk ke WA pemohon (klik 2x)"
                            onClick={() => handleApprove(r)}
                            className={`rounded-md p-1.5 transition-colors duration-200 ${confirmApprove === r.usg_id ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-700"}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          data-testid={`usg-delete-${r.usg_id}`}
                          title="Hapus data"
                          onClick={() => handleDelete(r)}
                          className={`rounded-md p-1.5 transition-colors duration-200 ${
                            confirmDelete === r.usg_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                          }`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
    <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="usg-edit-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Data Pemohon USG — {editItem?.name}</DialogTitle>
        </DialogHeader>
        {editItem && (
        <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3" data-testid="usg-edit-form">
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">Nama Lengkap *</Label>
            <Input data-testid="usg-edit-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. KTP / NIK</Label>
            <Input data-testid="usg-edit-nik-input" value={editForm.nik} onChange={(e) => setEditForm({ ...editForm, nik: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tanggal Lahir</Label>
            <Input type="date" data-testid="usg-edit-birth-date-input" value={editForm.birth_date} onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. WhatsApp</Label>
            <Input data-testid="usg-edit-phone-input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desa</Label>
            <Input data-testid="usg-edit-desa-input" value={editForm.desa} onChange={(e) => setEditForm({ ...editForm, desa: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">RT</Label>
              <Input data-testid="usg-edit-rt-input" value={editForm.rt} onChange={(e) => setEditForm({ ...editForm, rt: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RW</Label>
              <Input data-testid="usg-edit-rw-input" value={editForm.rw} onChange={(e) => setEditForm({ ...editForm, rw: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kecamatan</Label>
            <Input data-testid="usg-edit-kecamatan-input" value={editForm.kecamatan} onChange={(e) => setEditForm({ ...editForm, kecamatan: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kabupaten</Label>
            <Input data-testid="usg-edit-kabupaten-input" value={editForm.kabupaten} onChange={(e) => setEditForm({ ...editForm, kabupaten: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No. Rekam Medis (RM)</Label>
            <Input data-testid="usg-edit-rm-input" placeholder="diisi oleh petugas" value={editForm.rm} onChange={(e) => setEditForm({ ...editForm, rm: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger data-testid="usg-edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diajukan">Diajukan</SelectItem>
                <SelectItem value="disetujui">Disetujui</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="usg-edit-save" disabled={editSaving} className="col-span-2 bg-emerald-800 hover:bg-emerald-700 text-white">
            {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function Layanan() {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState("ambulance");
  const fileRef = useRef(null);
  const canExportLayanan = ["admin", "pengurus", "operator", "koordinator_layanan"].includes(user?.role);
  const canImportLayanan = ["admin", "pengurus"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const colCount = user?.role === "admin" ? 7 : 6;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get("/service-requests", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.date) {
      toast.error("Nama pasien dan tanggal wajib diisi");
      return;
    }
    setSaving(true);
    const payload = { ...form, day: dayFromDate(form.date) };
    try {
      if (editingId) {
        await api.put(`/service-requests/${editingId}`, payload);
        toast.success("Permohonan diperbarui");
      } else {
        const { data } = await api.post("/service-requests", payload);
        toast.success("Permohonan layanan tersimpan");
        const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
        const total = (data.wa_notifs || []).length;
        if (total > 0) {
          if (sent === total) toast.success(`Notifikasi WA terkirim ke ${sent} nomor (Keluarga, Koordinator, CS Admin)`);
          else toast.warning(`Notifikasi WA: ${sent}/${total} terkirim. ${(data.wa_notifs || []).find((w) => !w.ok)?.reason || ""}`);
        }
      }
      setEditingId(null);
      setForm(emptyForm);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.request_id);
    setForm({
      patient_name: r.patient_name,
      desa: r.desa || "",
      dukuh: r.dukuh || "",
      rt: r.rt || "",
      rw: r.rw || "",
      date: r.date,
      time: r.time || "",
      destination: r.destination,
      destination_detail: r.destination_detail || "",
      care_type: r.care_type,
      patient_condition: r.patient_condition,
      family_phone: r.family_phone || "",
      status: r.status || "baru",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.date) {
      toast.error("Nama pasien dan tanggal wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/service-requests/${editingId}`, { ...form, day: dayFromDate(form.date) });
      toast.success("Permohonan diperbarui");
      setEditOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (r, status) => {
    try {
      await api.patch(`/service-requests/${r.request_id}/status`, { status });
      toast.success(`Status diubah menjadi ${optionLabel(LAYANAN_STATUSES, status)}`);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (r) => {
    if (confirmDelete !== r.request_id) {
      setConfirmDelete(r.request_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/service-requests/${r.request_id}`);
      toast.success("Permohonan dihapus");
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleSrExport = async (format) => {
    try {
      const res = await api.get("/service-requests/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-permohonan-layanan.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data layanan berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data layanan");
    }
  };

  const handleSrTemplate = async () => {
    try {
      const res = await api.get("/service-requests/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "contoh-impor-layanan.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh format contoh");
    }
  };

  const handleSrImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/service-requests/import", fd);
      if (data.failed > 0) {
        toast.warning(`Impor: ${data.imported} berhasil, ${data.failed} gagal. ${data.errors?.[0] || ""}`);
      } else {
        toast.success(`${data.imported} permohonan berhasil diimpor`);
      }
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () => setSelected((prev) => (prev.length === items.length ? [] : items.map((r) => r.request_id)));
  const handleBulkDelete = async () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      setTimeout(() => setConfirmBulk(false), 3000);
      return;
    }
    try {
      const { data } = await api.post("/service-requests/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} permohonan dihapus`);
      setSelected([]);
      setConfirmBulk(false);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="layanan-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Permohonan Layanan</h1>
        <p className="text-sm text-slate-500 mt-1">Layanan antar pasien, Khitan, Pinjam Alat Kesehatan, Kartu Sehat & USG Gratis</p>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList data-testid="layanan-main-tabs">
          <TabsTrigger value="ambulance" data-testid="layanan-tab-ambulance">Ambulance / MLU</TabsTrigger>
          <TabsTrigger value="khitan" data-testid="layanan-tab-khitan">Khitan Sehat Gratis</TabsTrigger>
          <TabsTrigger value="alat" data-testid="layanan-tab-alat">Pinjam Alat Kesehatan</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="kartu" data-testid="layanan-tab-kartu">Kartu Sehat Gratis</TabsTrigger>
          )}
          {user?.role === "admin" && (
            <TabsTrigger value="usg" data-testid="layanan-tab-usg">Ibu Hamil & USG</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {mainTab === "alat" ? (
        <EquipmentLoans user={user} />
      ) : mainTab === "khitan" ? (
        <KhitanList user={user} />
      ) : mainTab === "kartu" && user?.role === "admin" ? (
        <KartuSehatList user={user} />
      ) : mainTab === "usg" && user?.role === "admin" ? (
        <UsgList user={user} />
      ) : (
      <div>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-lg flex-1">Daftar Permohonan ({items.length})</CardTitle>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleSrImport} data-testid="layanan-import-file-input" />
              {canImportLayanan && (
                <>
                  <Button type="button" variant="outline" size="sm" data-testid="layanan-template-button" onClick={handleSrTemplate}>
                    <Download className="mr-1.5 h-3.5 w-3.5" />Unduh Format Excel
                  </Button>
                  <Button type="button" variant="outline" size="sm" data-testid="layanan-import-button" onClick={() => fileRef.current?.click()}>
                    <Upload className="mr-1.5 h-3.5 w-3.5" />Impor Data Excel
                  </Button>
                </>
              )}
              {user?.role === "admin" && selected.length > 0 && (
                <Button type="button" size="sm" variant={confirmBulk ? "destructive" : "outline"} data-testid="layanan-bulk-delete-button" onClick={handleBulkDelete}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />{confirmBulk ? `Yakin? Hapus ${selected.length} Data` : `Hapus Terpilih (${selected.length})`}
                </Button>
              )}
              {canExportLayanan && (
                <>
                  <Button type="button" variant="outline" size="sm" data-testid="layanan-export-pdf-button" onClick={() => handleSrExport("pdf")}>
                    <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
                  </Button>
                  <Button type="button" variant="outline" size="sm" data-testid="layanan-export-excel-button" onClick={() => handleSrExport("xlsx")}>
                    <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
                  </Button>
                </>
              )}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="layanan-search-input"
                  placeholder="Cari pasien/desa..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-[200px]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[560px] overflow-auto">
              <Table data-testid="layanan-table">
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    {user?.role === "admin" && (
                      <TableHead className="w-8">
                        <input type="checkbox" data-testid="layanan-select-all" className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={items.length > 0 && selected.length === items.length} onChange={toggleSelectAll} />
                      </TableHead>
                    )}
                    <TableHead>Pasien</TableHead>
                    <TableHead>Jadwal</TableHead>
                    <TableHead>Tujuan</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin inline-block" />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="layanan-empty">
                        Belum ada permohonan layanan
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((r) => (
                      <TableRow key={r.request_id} data-testid={`layanan-row-${r.request_id}`}>
                        {user?.role === "admin" && (
                          <TableCell>
                            <input type="checkbox" data-testid={`layanan-select-${r.request_id}`} className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={selected.includes(r.request_id)} onChange={() => toggleSelect(r.request_id)} />
                          </TableCell>
                        )}
                        <TableCell>
                          <p className="text-sm font-medium text-slate-800">{r.patient_name}</p>
                          <p className="text-xs text-slate-400">
                            {[r.desa && `Desa ${r.desa}`, r.dukuh && `Dk. ${r.dukuh}`, (r.rt || r.rw) && `RT ${r.rt || "-"}/RW ${r.rw || "-"}`].filter(Boolean).join(", ")}
                          </p>
                          <p className="text-xs text-slate-400">{r.family_phone || ""}</p>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {r.day}, {formatDate(r.date)}
                          {r.time ? ` · ${r.time}` : ""}
                        </TableCell>
                        <TableCell className="text-sm">
                          {optionLabel(LAYANAN_DESTINATIONS, r.destination)}
                          {r.destination_detail ? ` - ${r.destination_detail}` : ""}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {optionLabel(LAYANAN_CARE_TYPES, r.care_type)} · {optionLabel(LAYANAN_CONDITIONS, r.patient_condition)}
                        </TableCell>
                        <TableCell>
                          <Select value={r.status || "baru"} onValueChange={(v) => handleStatusChange(r, v)} disabled={!canChangeLayananStatus(user?.role)}>
                            <SelectTrigger className={`h-7 w-[110px] text-xs font-semibold ${STATUS_COLORS[r.status || "baru"]}`} data-testid={`layanan-status-${r.request_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LAYANAN_STATUSES.map((s) => (
                                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {user?.role === "admin" && (
                            <button
                              data-testid={`layanan-edit-${r.request_id}`}
                              title="Ubah data"
                              onClick={() => handleEdit(r)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            )}
                            {canEditData(user?.role) && (
                            <button
                              data-testid={`layanan-delete-${r.request_id}`}
                              onClick={() => handleDelete(r)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === r.request_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="layanan-edit-dialog">
            <DialogHeader>
              <DialogTitle>Ubah Permohonan Layanan</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3" data-testid="layanan-edit-form">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Nama Pasien *</Label>
                <Input data-testid="layanan-edit-name-input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Desa</Label>
                <Input data-testid="layanan-edit-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dukuh</Label>
                <Input data-testid="layanan-edit-dukuh-input" value={form.dukuh} onChange={(e) => setForm({ ...form, dukuh: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">RT</Label>
                <Input data-testid="layanan-edit-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">RW</Label>
                <Input data-testid="layanan-edit-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tanggal *</Label>
                <Input type="date" data-testid="layanan-edit-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Jam</Label>
                <Input type="time" data-testid="layanan-edit-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tujuan</Label>
                <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                  <SelectTrigger data-testid="layanan-edit-destination-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYANAN_DESTINATIONS.map((d) => (
                      <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nama RS/Klinik/Tujuan</Label>
                <Input data-testid="layanan-edit-destination-detail-input" value={form.destination_detail} onChange={(e) => setForm({ ...form, destination_detail: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Keterangan</Label>
                <Select value={form.care_type} onValueChange={(v) => setForm({ ...form, care_type: v })}>
                  <SelectTrigger data-testid="layanan-edit-caretype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYANAN_CARE_TYPES.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Kondisi Pasien</Label>
                <Select value={form.patient_condition} onValueChange={(v) => setForm({ ...form, patient_condition: v })}>
                  <SelectTrigger data-testid="layanan-edit-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYANAN_CONDITIONS.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">No. WA Keluarga</Label>
                <Input data-testid="layanan-edit-phone-input" value={form.family_phone} onChange={(e) => setForm({ ...form, family_phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="layanan-edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYANAN_STATUSES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="layanan-edit-save" disabled={saving} className="col-span-2 bg-emerald-800 hover:bg-emerald-700 text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      )}
    </div>
  );
}
